<template>
	<SuiDataTable
		class="retainer-period-list js-retainer-period-list-table"
		:class="{
			'retainer-period-list--loading': isLoading,
		}"
		data-testId="retainer-period-list"
		:loading="isLoading"
		:hide-header="isLoading"
		:col-definition="colDefinition"
	>
		<SuiDataTableRow
			v-slot="{ row: period, index }"
			:rows="retainerPeriodList"
		>
			<SuiDataTableCell
				class="retainer-period-modal__no"
				role="button"
				:class="{
					'retainer-period-modal__active-period':
						period.id && period.id === retainerPeriodId,
				}"
				@click="navigateToPeriodDetailedView(period)"
			>
				{{ period.isSummaryRow ? '' : index + 1 }}
			</SuiDataTableCell>
			<SuiDataTableCell
				role="button"
				@click="navigateToPeriodDetailedView(period)"
			>
				<div
					v-if="period.isSummaryRow && timeOutsideBillablePeriod > 0"
					v-tooltip="retainerPeriodWarningMsg"
					:aria-label="retainerPeriodWarningMsg"
					data-testId="retainer-modal-warning"
					class="retainer-period-modal__warning"
				>
					<SuiIcon name="icon-alert-filled-16" />
				</div>
				<template v-else-if="!period.isSummaryRow">
					<strong class="retainer-period-modal__period-title">
						{{ period.name || period.defaultName }}
					</strong>
					<div class="retainer-period-modal__period-dates">
						{{ formatDateNoCurrentYear(period.startDate) }} -
						{{ formatDateNoCurrentYear(period.endDate) }}
					</div>
				</template>
			</SuiDataTableCell>
			<SuiDataTableCell
				role="button"
				@click="navigateToPeriodDetailedView(period)"
			>
				<strong
					class="retainer-period-modal__period-remaining-hours"
					:class="{
						'retainer-period-modal__period-remaining-hours--no-value':
							!period.remainingDuration,
					}"
				>
					{{
						formatLongDuration({
							duration: period.remainingDuration,
							removeMinutesOnFullHours: true,
						})
					}}
				</strong>
				<div class="retainer-period-modal__period-total-hours">
					{{
						formatLongDuration({
							duration: period.duration,
							removeMinutesOnFullHours: true,
						})
					}}
				</div>
			</SuiDataTableCell>
			<SuiDataTableCell
				v-if="showBudgetColumn"
				role="button"
				@click="navigateToPeriodDetailedView(period)"
			>
				<strong
					class="retainer-period-modal__period-actual-budget"
					:class="{
						'retainer-period-modal__period-actual-budget--no-value':
							!period.actualSum,
					}"
				>
					{{ getActualBudgetDisplayValue(period) }}
				</strong>
				<div class="retainer-period-modal__period-total-budget">
					{{ formatBudget(period.sum) }} {{ displayCurrency }}
				</div>
			</SuiDataTableCell>
			<SuiDataTableCell
				role="button"
				@click="navigateToPeriodDetailedView(period)"
			>
				<SuiButton
					v-tooltip="{
						content: periodNavigationTooltip(period),
						delay: { show: 300 },
					}"
					type="button"
					class="retainer-period-modal__period-action"
				>
					<SuiIcon name="icon-longarrow-16" />
				</SuiButton>
			</SuiDataTableCell>
		</SuiDataTableRow>
	</SuiDataTable>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue';
import {
	SuiButton,
	SuiDataTable,
	SuiDataTableCell,
	type SuiDataTableColumnType,
	SuiDataTableRow,
	SuiIcon,
} from '@developers/scoro-ui';
import { type RetainerPeriod } from '@/generated/graphql';
import { type RetainerPeriodItem, type RetainerPeriodSummary } from './types';
import { useFormatter, useTranslation } from '@/composables';

export default defineComponent({
	components: {
		SuiIcon,
		SuiButton,
		SuiDataTableCell,
		SuiDataTableRow,
		SuiDataTable,
	},
	props: {
		retainerPeriods: {
			type: Array as PropType<RetainerPeriodItem[]>,
			required: true,
		},
		isLoading: {
			type: Boolean,
			required: true,
		},
		timeOutsideBillablePeriod: {
			type: Number,
			required: true,
		},
		projectId: {
			type: String,
			required: true,
		},
		retainerPeriodId: {
			type: String,
			default: undefined,
		},
		retainerSummary: {
			type: Object as PropType<RetainerPeriodSummary>,
			required: true,
		},
		projectColor: {
			type: String,
			required: true,
		},
		displayCurrency: {
			type: String,
			required: true,
		},
		showBudgetColumn: {
			type: Boolean,
			required: true,
		},
	},
	emits: ['selected'],
	setup(props, { emit }) {
		const { t } = useTranslation();
		const { formatLongDuration, formatDateNoCurrentYear, formatNumber } =
			useFormatter();

		const colDefinition = computed<SuiDataTableColumnType[]>(() => {
			const cols = [
				{
					id: 'no',
					title: '',
					width: '0',
				},
				{
					id: 'name',
					title: t('retainer-modal__billing-period'),
				},
				{
					id: 'actual-hours',
					title: t('retainer-modal__remaining-hours'),
					width: '20%',
				},
			];

			if (props.showBudgetColumn) {
				cols.push({
					id: 'actual-budget',
					title: `${t('retainer-modal__remaining-budget')} ${
						props.displayCurrency
					}`,
					width: '20%',
				});
			}

			cols.push({
				id: 'modify',
				title: '',
				width: '10%',
			});

			return cols;
		});

		const retainerPeriodList = computed<RetainerPeriodItem[]>(() => [
			...props.retainerPeriods,
			props.retainerSummary,
		]);

		const navigateToPeriodDetailedView = (period: RetainerPeriodItem) => {
			emit('selected', period.isSummaryRow ? -1 : period.id);
		};

		const retainerPeriodWarningMsg = computed(() => {
			return t('retainer-modal__billing-periods-warning', {
				duration: formatLongDuration({
					duration: props.timeOutsideBillablePeriod,
					removeMinutesOnFullHours: true,
				}),
			});
		});

		const formatBudget = (budget: number, decimalPrecision = 2) => {
			return formatNumber(budget, {
				forceDecimalForInteger: false,
				decimalPrecision,
			});
		};

		const getActualBudgetDisplayValue = (
			period: RetainerPeriod
		): string => {
			const sum = period.sum || 0;
			const actualSum = period.actualSum || 0;

			if (sum > 0 && actualSum > 0) {
				const usedBudgetPercent = (actualSum / sum) * 100;
				return `${formatBudget(usedBudgetPercent, 0)}%`;
			}

			return '-';
		};

		const periodNavigationTooltip = (period: RetainerPeriodItem) => {
			return period.isSummaryRow
				? t('retainer-modal__navigate-to-all-periods')
				: t('retainer-modal__navigate-to-period');
		};

		return {
			retainerPeriodList,
			navigateToPeriodDetailedView,
			retainerPeriodWarningMsg,
			getActualBudgetDisplayValue,
			formatDateNoCurrentYear,
			formatLongDuration,
			formatNumber,
			formatBudget,
			colDefinition,
			periodNavigationTooltip,
		};
	},
});
</script>

<style lang="scss" scoped>
.retainer-period-modal {
	&__no {
		vertical-align: top;
	}

	&__warning {
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 50%;
		width: 4.5rem;
		height: 4.5rem;
		background: $orange-10;

		.sui-icon {
			fill: $orange-50;
		}
	}

	&__period-dates,
	&__period-total-hours,
	&__period-total-budget {
		font-size: 1.5rem;
		line-height: 2rem;
		color: $neutral-70;
		margin-top: -0.125rem;
	}

	&__period-remaining-hours,
	&__period-actual-budget {
		display: block;
		font-size: 1.625rem;
		line-height: 2.5rem;

		&--no-value {
			color: $neutral-70;
		}
	}

	&__period-title {
		display: inline-block;
		font-size: 1.75rem;
		width: 100%;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__active-period {
		border-left-color: v-bind(projectColor);
	}
}

.retainer-period-list {
	line-height: 2.25rem;

	.sui-data-table-col {
		max-width: 50rem;
		padding-top: 2rem;
		padding-bottom: 2rem;
	}
}
</style>

<style lang="scss">
.retainer-period-list {
	line-height: 2.25rem;

	&:not(.retainer-period-list--loading) {
		.sui-data-table-row {
			cursor: pointer;

			&:hover {
				background: $neutral-10;
			}
		}
	}
}
</style>
